import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantBranding.css";
import { API_BASE } from "../../../ApiUrl";
import BrandingTabs from "./BrandingTabs";

const API_URL = `${API_BASE}/tenant-brandings/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const buildMediaUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const origin = API_BASE.replace(/\/api\/?$/, "").replace(/\/$/, "");
  return `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
};

// Default colour rows when nothing exists
const defaultColourRows = () => [
  { key: "primary", value: "#2563eb" },
  { key: "secondary", value: "#1e40af" },
  { key: "accent", value: "#f59e0b" }
];

// Convert array of { key, value } → { key: value }
const rowsToObject = (rows) => {
  const obj = {};
  rows.forEach((row) => {
    const k = (row.key || "").trim();
    const v = (row.value || "").trim();
    if (k) obj[k] = v;
  });
  return obj;
};

// Convert object → array of { key, value }
const objectToRows = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return defaultColourRows();
  }
  const entries = Object.entries(obj);
  if (!entries.length) return defaultColourRows();
  return entries.map(([key, value]) => ({
    key,
    value: value == null ? "" : String(value)
  }));
};

const emptyBranding = {
  id: null,
  report_header: "",
  report_footer: "",
  disclaimer_text: ""
};

const AddTenantBranding = () => {
  const navigate = useNavigate();

  const [branding, setBranding] = useState({ ...emptyBranding });
  const [savedBranding, setSavedBranding] = useState({ ...emptyBranding });

  const [colourRows, setColourRows] = useState(defaultColourRows);
  const [savedColourRows, setSavedColourRows] = useState(defaultColourRows);

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savedLogoUrl, setSavedLogoUrl] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Fetch existing branding ----
  useEffect(() => {
    const fetchExisting = async () => {
      const tenantUser = getTenantUser();

      if (!tenantUser?.tenant_id) {
        setLoadingExisting(false);
        return;
      }

      try {
        setLoadingExisting(true);

        const response = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantUser.tenant_id,
            tables: ["tenant_brandings"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data;

        let rows = [];
        if (result?.data?.tenant_brandings) {
          rows = result.data.tenant_brandings;
        } else if (result?.tenant_brandings) {
          rows = result.tenant_brandings;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }

        const matched = rows.length ? rows[0] : null;

        if (matched) {
          // Normalize colours — could be object, array, or JSON string
          let coloursObj = {};
          if (matched.colours !== null && matched.colours !== undefined) {
            if (typeof matched.colours === "string") {
              try {
                coloursObj = JSON.parse(matched.colours);
              } catch {
                coloursObj = {};
              }
            } else if (
              typeof matched.colours === "object" &&
              !Array.isArray(matched.colours)
            ) {
              coloursObj = matched.colours;
            }
          }

          const rowsFromColours = objectToRows(coloursObj);

          const loaded = {
            id: matched.id,
            report_header: matched.report_header || "",
            report_footer: matched.report_footer || "",
            disclaimer_text: matched.disclaimer_text || ""
          };

          setBranding(loaded);
          setSavedBranding(loaded);
          setColourRows(rowsFromColours);
          setSavedColourRows(rowsFromColours);

          if (matched.logo) {
            const fullUrl = buildMediaUrl(matched.logo);
            setSavedLogoUrl(fullUrl);
            setLogoPreview(fullUrl);
          }
        } else {
          const defaults = defaultColourRows();
          setBranding({ ...emptyBranding });
          setSavedBranding({ ...emptyBranding });
          setColourRows(defaults);
          setSavedColourRows(defaults);
        }
      } catch (error) {
        console.error("Error fetching tenant branding:", error);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Branding",
          text: "Could not fetch tenant branding."
        });
        const defaults = defaultColourRows();
        setBranding({ ...emptyBranding });
        setSavedBranding({ ...emptyBranding });
        setColourRows(defaults);
        setSavedColourRows(defaults);
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBranding((prev) => ({ ...prev, [name]: value }));
  };

  // ---- Colour row handlers ----
  const handleColourRowChange = (index, field, value) => {
    setColourRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addColourRow = () => {
    setColourRows((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeColourRow = (index) => {
    setColourRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ key: "", value: "" }];
    });
  };

  // ---- Logo handlers ----
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    setRemoveLogo(false);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(savedLogoUrl || null);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
    const input = document.getElementById("tenant-branding-logo-input");
    if (input) input.value = "";
  };

  // ---- Validation ----
  const validate = () => {
    // At least one non-empty colour key required
    const hasAnyColour = colourRows.some((row) => (row.key || "").trim());
    if (!hasAnyColour) {
      Swal.fire({
        icon: "warning",
        title: "No Colours Entered",
        text: "Please add at least one colour (e.g. primary → #2563eb)."
      });
      return false;
    }

    // Check for blank value on any filled key
    for (let i = 0; i < colourRows.length; i++) {
      const row = colourRows[i];
      const k = (row.key || "").trim();
      const v = (row.value || "").trim();
      if (k && !v) {
        Swal.fire({
          icon: "warning",
          title: "Missing Colour Value",
          text: `Please provide a value for "${k}".`
        });
        return false;
      }
    }

    // Duplicate key check
    const keys = colourRows
      .map((r) => (r.key || "").trim().toLowerCase())
      .filter(Boolean);
    const dupIdx = keys.findIndex((k, i) => keys.indexOf(k) !== i);
    if (dupIdx !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Colour Key",
        text: `Colour key "${colourRows[dupIdx].key.trim()}" is used more than once.`
      });
      return false;
    }

    if (logoFile) {
      const MAX_BYTES = 2 * 1024 * 1024;
      if (logoFile.size > MAX_BYTES) {
        Swal.fire({
          icon: "warning",
          title: "Logo Too Large",
          text: "Logo file must be 2 MB or smaller."
        });
        return false;
      }
      if (!logoFile.type.startsWith("image/")) {
        Swal.fire({
          icon: "warning",
          title: "Invalid File Type",
          text: "Logo must be an image file."
        });
        return false;
      }
    }

    return true;
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setBranding({ ...savedBranding });
    setColourRows(savedColourRows.map((r) => ({ ...r })));
    setLogoFile(null);
    setRemoveLogo(false);
    setLogoPreview(savedLogoUrl || null);
    const input = document.getElementById("tenant-branding-logo-input");
    if (input) input.value = "";
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditing) return;

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID is missing from tenant_user."
      });
      return;
    }

    if (!validate()) return;

    try {
      setLoading(true);

      const coloursObject = rowsToObject(colourRows);

      const formData = new FormData();
      formData.append("colours", JSON.stringify(coloursObject));
      formData.append("report_header", branding.report_header || "");
      formData.append("report_footer", branding.report_footer || "");
      formData.append("disclaimer_text", branding.disclaimer_text || "");
      formData.append("tenant", tenantUser.tenant_id);
      formData.append("user", tenantUser.user_id);

      if (logoFile) {
        formData.append("logo", logoFile);
      } else if (removeLogo) {
        formData.append("logo", "");
      }

      const config = {
        headers: { "Content-Type": "multipart/form-data" }
      };

      let newLogoUrl = savedLogoUrl;

      if (branding.id) {
        const response = await axios.put(
          `${API_URL}${branding.id}/`,
          formData,
          config
        );

        const respData = response.data?.data || response.data;
        if (respData?.logo) newLogoUrl = buildMediaUrl(respData.logo);
        else if (removeLogo) newLogoUrl = null;
      } else {
        const response = await axios.post(API_URL, formData, config);

        const respData = response.data?.data || response.data;
        const newId = respData?.id || response.data?.id;

        if (newId) {
          setBranding((prev) => ({ ...prev, id: newId }));
          setSavedBranding((prev) => ({ ...prev, id: newId }));
        }
        if (respData?.logo) newLogoUrl = buildMediaUrl(respData.logo);
      }

      const refreshed = { ...branding };
      setSavedBranding(refreshed);
      setSavedColourRows(colourRows.map((r) => ({ ...r })));
      setSavedLogoUrl(newLogoUrl);
      setLogoPreview(newLogoUrl);
      setLogoFile(null);
      setRemoveLogo(false);
      setIsEditing(false);

      await Swal.fire({
        icon: "success",
        title: "Saved Successfully",
        confirmButtonText: "OK"
      });
    } catch (error) {
      console.error("Save Tenant Branding Error:", error);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: `Unable to save tenant branding. ${extractError(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-branding-page">
          <BrandingTabs />

          <div className="tenant-branding-page-header">
            <div>
              <h2>Tenant Branding</h2>
            </div>
          </div>

          {loadingExisting ? (
            <div className="tenant-branding-loading">
              Loading tenant branding...
            </div>
          ) : (
            <form className="tenant-branding-card" onSubmit={handleSubmit}>


              <div className="tenant-branding-form-body">
                <div className="tenant-branding-form-grid">
                  {/* Logo */}
                  <div className="tenant-branding-form-group tenant-branding-form-group--full">
                    <label>Logo</label>

                    <div className="tenant-branding-logo-row">
                      <div className="tenant-branding-logo-preview">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Tenant logo preview" />
                        ) : (
                          <span className="tenant-branding-logo-placeholder">
                            No logo
                          </span>
                        )}
                      </div>

                      <div className="tenant-branding-logo-controls">
                        <input
                          id="tenant-branding-logo-input"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          disabled={!isEditing}
                        />

                        {isEditing && (logoPreview || logoFile) && (
                          <button
                            type="button"
                            className="tenant-branding-logo-remove-btn"
                            onClick={handleRemoveLogo}
                          >
                            Remove logo
                          </button>
                        )}

                        <p className="tenant-branding-hint">
                          PNG, JPG or SVG. Recommended square or wide format.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Colours — key / value rows */}
                  <div className="tenant-branding-form-group tenant-branding-form-group--full">
                    <label>Colours</label>

                    <div className="tenant-branding-colours">
                      {colourRows.map((row, index) => (
                        <div
                          className="tenant-branding-colour-row"
                          key={index}
                        >
                          <input
                            type="text"
                            className="tenant-branding-colour-key"
                            value={row.key}
                            onChange={(e) =>
                              handleColourRowChange(
                                index,
                                "key",
                                e.target.value
                              )
                            }
                            disabled={!isEditing}
                            placeholder="e.g. primary"
                            maxLength={50}
                          />

                          <input
                            type="text"
                            className="tenant-branding-colour-value"
                            value={row.value}
                            onChange={(e) =>
                              handleColourRowChange(
                                index,
                                "value",
                                e.target.value
                              )
                            }
                            disabled={!isEditing}
                            placeholder="e.g. #2563eb"
                            maxLength={50}
                          />

                          {isEditing && colourRows.length > 1 && (
                            <button
                              type="button"
                              className="tenant-branding-colour-remove"
                              onClick={() => removeColourRow(index)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}

                      {isEditing && (
                        <button
                          type="button"
                          className="tenant-branding-colour-add"
                          onClick={addColourRow}
                        >
                          + Add Colour
                        </button>
                      )}
                    </div>

                    <p className="tenant-branding-hint">
                      Enter colour tokens like primary, secondary, accent with
                      their hex values. They are sent to the API as a JSON
                      object.
                    </p>
                  </div>

                  {/* Report Header */}
                  <div className="tenant-branding-form-group tenant-branding-form-group--full">
                    <label>Report Header</label>
                    <textarea
                      name="report_header"
                      value={branding.report_header}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="Text shown at the top of generated reports"
                      rows={3}
                    />
                  </div>

                  {/* Report Footer */}
                  <div className="tenant-branding-form-group tenant-branding-form-group--full">
                    <label>Report Footer</label>
                    <textarea
                      name="report_footer"
                      value={branding.report_footer}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="Text shown at the bottom of generated reports"
                      rows={3}
                    />
                  </div>

                  {/* Disclaimer */}
                  <div className="tenant-branding-form-group tenant-branding-form-group--full">
                    <label>Disclaimer Text</label>
                    <textarea
                      name="disclaimer_text"
                      value={branding.disclaimer_text}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="Legal footer text shown on generated documents"
                      rows={4}
                    />
                  </div>
                </div>
              </div>

              <div className="tenant-branding-form-actions">
                {!isEditing ? (
                  <button
                    type="button"
                    className="tenant-branding-save-btn"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="tenant-branding-cancel-btn"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="tenant-branding-save-btn"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Branding"}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantBranding;